import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initSentry } from "./lib/sentry";
import { initI18n } from "./lib/i18n";

initSentry();
import { createBrowserRouter, RouterProvider } from "react-router";

import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/source-serif-4/400-italic.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/fredoka/400.css";
import "@fontsource/fredoka/500.css";
import "@fontsource/fredoka/600.css";
import "@fontsource/fredoka/700.css";

import "./globals.css";

import { AppLayout } from "./components/AppLayout";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";
import { ForgotPasswordPage } from "./routes/forgot-password";
import { AuthActionPage } from "./routes/auth-action";
import { SettingsPage } from "./routes/settings";
import { NotFoundPage } from "./routes/not-found";
import { RootRedirect } from "./components/RootRedirect";
import { LockedModuleGuard } from "./components/LockedModuleGuard";
import { requireSession } from "./lib/session-loader";

// ── New top-level pages ───────────────────────────────────────────────────────
import { VandaagPage } from "./routes/vandaag/index";
import { OefenenPage } from "./routes/oefenen";
import { GrammaticaIndexPage } from "./routes/grammatica/index";
import { GrammaticaReaderPage } from "./routes/grammatica/slug";
import { WelkomBezoekerPage } from "./routes/welkom-bezoeker";

// ── Existing pages (kept intact) ─────────────────────────────────────────────
import { HomePage } from "./routes/spreken/home";
import { ExamPage } from "./routes/spreken/exam";
import { ReportPage } from "./routes/spreken/report";
import { GeschiedenisPage } from "./routes/spreken/geschiedenis";
import { WoordenIndexPage } from "./routes/spreken/woorden";
import { WoordenTopicPage } from "./routes/spreken/woorden/topic";
import { LerenIndexPage } from "./routes/spreken/leren";
import { LerenTopicPage } from "./routes/spreken/leren/slug";
import { WelkomPage } from "./routes/welkom";
import { SchrijvenHomePage } from "./routes/schrijven/home";
import { SchrijvenExamPage } from "./routes/schrijven/exam";
import { SchrijvenPracticePage } from "./routes/schrijven/practice";
import { SchrijvenReportPage } from "./routes/schrijven/report";
import { SchrijvenGeschiedenisPage } from "./routes/schrijven/geschiedenis";
import { SchrijvenLerenIndexPage } from "./routes/schrijven/leren/index";
import { SchrijvenLerenTopicPage } from "./routes/schrijven/leren/slug";
import { AdminUsersPage } from "./routes/admin";
import { AdminHealthPage } from "./routes/admin/health";
import { AdminEconomicsPage } from "./routes/admin/health-economics";
import { AdminCachePage } from "./routes/admin/cache";
import { AdminUserDetailPage } from "./routes/admin/user-detail";
import { AdminModulesPage } from "./routes/admin/modules";
import { AdminSchrijvenVariantsPage } from "./routes/admin/schrijven/variants";
import { AdminSchrijvenVariantNewPage } from "./routes/admin/schrijven/variant-new";
import { AdminSchrijvenVariantEditPage } from "./routes/admin/schrijven/variant-edit";
import { AdminSchrijvenReviewQueuePage } from "./routes/admin/schrijven/review-queue";

const router = createBrowserRouter([
  // ── Public / auth routes (no shell) ────────────────────────────────────────
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/__/auth/action", element: <AuthActionPage /> },
  // Public marketing landing — outside the authed shell
  { path: "/welkom-bezoeker", element: <WelkomBezoekerPage /> },

  // ── Authed app shell ────────────────────────────────────────────────────────
  {
    path: "/",
    element: <AppLayout />,
    loader: requireSession,
    children: [
      // Root → Vandaag (or /welkom for new users)
      { index: true, element: <RootRedirect /> },

      // ── New routes ──────────────────────────────────────────────────────────
      { path: "vandaag", element: <VandaagPage /> },
      { path: "oefenen", element: <OefenenPage /> },
      // /woorden aliased to the existing WoordenIndexPage
      { path: "woorden", element: <WoordenIndexPage /> },
      { path: "woorden/:topic", element: <WoordenTopicPage /> },
      // /voortgang aliased to the existing GeschiedenisPage (interim)
      { path: "voortgang", element: <GeschiedenisPage /> },
      { path: "grammatica", element: <GrammaticaIndexPage /> },
      { path: "grammatica/:slug", element: <GrammaticaReaderPage /> },

      // ── Onboarding (chromeless — AppLayout detects /welkom prefix) ──────────
      { path: "welkom", element: <WelkomPage /> },

      // ── Settings ────────────────────────────────────────────────────────────
      { path: "settings", element: <SettingsPage /> },

      // ── Spreken (existing — kept intact) ───────────────────────────────────
      { path: "spreken", element: <HomePage /> },
      { path: "spreken/exam/:sessionId", element: <ExamPage /> },
      { path: "spreken/report/:sessionId", element: <ReportPage /> },
      { path: "spreken/geschiedenis", element: <GeschiedenisPage /> },
      // /spreken/woorden kept alive (bookmark / deep-link compat)
      { path: "spreken/woorden", element: <WoordenIndexPage /> },
      { path: "spreken/woorden/:topic", element: <WoordenTopicPage /> },
      // /spreken/leren kept alive (report LerenLink deep-links)
      { path: "spreken/leren", element: <LerenIndexPage /> },
      { path: "spreken/leren/:slug", element: <LerenTopicPage /> },

      // ── Schrijven (existing — kept intact) ─────────────────────────────────
      { path: "schrijven", element: <SchrijvenHomePage /> },
      { path: "schrijven/exam", element: <SchrijvenExamPage /> },
      { path: "schrijven/practice/:taskType", element: <SchrijvenPracticePage /> },
      { path: "schrijven/report/:sessionId", element: <SchrijvenReportPage /> },
      { path: "schrijven/geschiedenis", element: <SchrijvenGeschiedenisPage /> },
      // /schrijven/leren kept alive (report / LerenLink deep-links)
      { path: "schrijven/leren", element: <SchrijvenLerenIndexPage /> },
      { path: "schrijven/leren/:slug", element: <SchrijvenLerenTopicPage /> },

      // ── Coming-soon module guards ───────────────────────────────────────────
      { path: "lezen/*", element: <LockedModuleGuard>{null}</LockedModuleGuard> },
      { path: "luisteren/*", element: <LockedModuleGuard>{null}</LockedModuleGuard> },
      { path: "knm/*", element: <LockedModuleGuard>{null}</LockedModuleGuard> },

      // ── Admin ───────────────────────────────────────────────────────────────
      { path: "admin", element: <AdminUsersPage /> },
      { path: "admin/cache", element: <AdminCachePage /> },
      { path: "admin/health", element: <AdminHealthPage /> },
      { path: "admin/health/economics", element: <AdminEconomicsPage /> },
      { path: "admin/users/:id", element: <AdminUserDetailPage /> },
      { path: "admin/modules", element: <AdminModulesPage /> },
      { path: "admin/schrijven/variants", element: <AdminSchrijvenVariantsPage /> },
      { path: "admin/schrijven/variants/new", element: <AdminSchrijvenVariantNewPage /> },
      { path: "admin/schrijven/variants/:id", element: <AdminSchrijvenVariantEditPage /> },
      { path: "admin/schrijven/review-queue", element: <AdminSchrijvenReviewQueuePage /> },
    ],
  },

  { path: "*", element: <NotFoundPage /> },
]);

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

async function bootstrap() {
  // Cookie is the only locale signal available at app shell mount. The
  // SessionUser loads after the React tree mounts (via requireSession),
  // and AppLayout's reconciliation effect swaps to user.preferredLanguage
  // if it differs from the cookie.
  const cookieLocale = readCookie("a2easy_lang") ?? "en";
  await initI18n(cookieLocale);
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

void bootstrap();
